import { useMemo, useCallback, useState } from "react";
import { useAccountant } from "./AccountantContext";
import FilterBar from "./FilterBar";
import EditableCell from "./EditableCell";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { getPropertyPricing, updatePropertyPricing } from "@/service/PricingService";

const EDITABLE_PRICE_COLS = [
    { key: 'total_override', label: 'Total Price' },
    { key: 'labour_price', label: 'Labour Price' },
    { key: 'labour_override', label: 'Team Price' },
    { key: 'linens_price', label: 'Linens Price' },
    { key: 'consumable_price', label: 'Consumable Price' },
];

const headCellSx = { fontWeight: 500, whiteSpace: 'nowrap', fontSize: 13, fontStyle: 'italic' };
const bodyCellSx = { fontSize: 13, py: 0.5, whiteSpace: 'nowrap' };
const truncatedCellSx = { ...bodyCellSx, maxWidth: 200, overflow: 'hidden', textOverflow: 'clip' };
const agentCellSx = { ...bodyCellSx, maxWidth: 120, overflow: 'hidden', textOverflow: 'clip' };
const priceHeadSx = { ...headCellSx, width: 80, minWidth: 80, maxWidth: 80, overflow: 'hidden', textOverflow: 'clip' };
const priceCellSx = { ...bodyCellSx, width: 80, minWidth: 80, maxWidth: 80 };
const paginationSx = { '& .MuiTablePagination-toolbar': { alignItems: 'baseline' } };

export default function PropertyPricingTab() {
    const [state, dispatch] = useAccountant();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const filteredRows = useMemo(() => {
        return state.propertyPrice
            .filter(it => it.full_address?.toLowerCase().includes(state.searchAddress.toLowerCase()))
            .filter(it => it.agent_name ? it.agent_name.toLowerCase().includes(state.searchAgent.toLowerCase()) : state.searchAgent === '');
    }, [state.propertyPrice, state.searchAddress, state.searchAgent]);

    const refreshPropertyPrices = useCallback(() => {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_NOTIFICATION', payload: null });
        getPropertyPricing(state.filterStartDate, state.filterEndDate, state.filterRegion)
            .then(res => { if (!res.ok) throw new Error('Failed to load property pricing'); return res.json(); })
            .then(data => {
                dispatch({ type: 'SET_PROPERTY_PRICING', payload: data });
                dispatch({ type: 'SET_LOADING', payload: false });
            })
            .catch(err => {
                dispatch({ type: 'SET_LOADING', payload: false });
                dispatch({ type: 'SET_NOTIFICATION', payload: { message: err.message, isSuccess: false } });
            });
    }, [dispatch, state.filterStartDate, state.filterEndDate, state.filterRegion]);

    const handleUpdateValue = useCallback((propertyId, cell, oldValue, value) => {
        const updates = { [cell]: value };
        if (cell === 'myob_profile_id') {
            updates.myob_business_id = null;
            updates.myob_contact_id = null;
        } else if (cell === 'myob_business_id') {
            updates.myob_contact_id = null;
        }
        Object.entries(updates).forEach(([c, v]) => {
            dispatch({ type: 'UPDATE_PROPERTY_CELL', payload: { propertyId, cell: c, value: v } });
        });
        dispatch({ type: 'SET_LOADING', payload: true });
        updatePropertyPricing({ property_id: propertyId, ...updates })
            .then(() => dispatch({ type: 'SET_LOADING', payload: false }))
            .catch(err => {
                dispatch({ type: 'UPDATE_PROPERTY_CELL', payload: { propertyId, cell, value: oldValue } });
                dispatch({ type: 'SET_LOADING', payload: false });
                dispatch({ type: 'SET_NOTIFICATION', payload: { message: err.message, isSuccess: false } });
            });
    }, [dispatch]);

    const getBusinessesForProfile = (profileId) => {
        if (!state.businessesAndAccounts || !profileId) return [];
        return state.businessesAndAccounts[profileId] || [];
    };

    const getContactsForBusiness = (profileId, businessId) => {
        const businesses = getBusinessesForProfile(profileId);
        const business = businesses.find(b => b.uid === businessId);
        return business?.contacts || [];
    };

    const totalColSpan = 6 + EDITABLE_PRICE_COLS.length;

    return (
        <div>
            <FilterBar showRegionSelector onRefresh={refreshPropertyPrices} />

            <TableContainer>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...headCellSx, minWidth: 180 }}>Address</TableCell>
                            <TableCell sx={headCellSx}>Agent Name</TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 140 }}>MyOB Profile</TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 140 }}>MyOB Business</TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 140 }}>MyOB Contact</TableCell>
                            <TableCell sx={headCellSx}>Next Job Date</TableCell>
                            {EDITABLE_PRICE_COLS.map(col => (
                                <TableCell key={col.key} sx={priceHeadSx} title={col.label}>{col.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(row => (
                            <TableRow key={row.property_id} hover>
                                <TableCell sx={truncatedCellSx} title={row.full_address}>{row.full_address}</TableCell>
                                <TableCell sx={agentCellSx} title={row.agent_name}>{row.agent_name}</TableCell>
                                <TableCell sx={bodyCellSx}>
                                    <FormControl variant="standard" fullWidth size="small">
                                        <Select
                                            value={row.myob_profile_id || ''}
                                            onChange={(e) => handleUpdateValue(row.property_id, 'myob_profile_id', row.myob_profile_id, e.target.value)}
                                            sx={{ fontSize: 13 }}
                                        >
                                            {state.myobProfiles.map(profile => (
                                                <MenuItem key={profile.uid} value={profile.uid}>{profile.username}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell sx={bodyCellSx}>
                                    <FormControl variant="standard" fullWidth size="small">
                                        <Select
                                            value={row.myob_business_id || ''}
                                            onChange={(e) => handleUpdateValue(row.property_id, 'myob_business_id', row.myob_business_id, e.target.value)}
                                            sx={{ fontSize: 13 }}
                                        >
                                            {getBusinessesForProfile(row.myob_profile_id).map(biz => (
                                                <MenuItem key={biz.uid} value={biz.uid}>{biz.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell sx={bodyCellSx}>
                                    <FormControl variant="standard" fullWidth size="small">
                                        <Select
                                            value={row.myob_contact_id || ''}
                                            onChange={(e) => handleUpdateValue(row.property_id, 'myob_contact_id', row.myob_contact_id, e.target.value)}
                                            sx={{ fontSize: 13 }}
                                        >
                                            {getContactsForBusiness(row.myob_profile_id, row.myob_business_id).map(contact => (
                                                <MenuItem key={contact.uid} value={contact.uid}>{contact.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell sx={bodyCellSx}>{row.next_job_date}</TableCell>
                                {EDITABLE_PRICE_COLS.map(col => (
                                    <TableCell key={col.key} sx={priceCellSx}>
                                        <EditableCell value={row[col.key]} type="number" onChange={(val) => handleUpdateValue(row.property_id, col.key, row[col.key], val)} />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                        {filteredRows.length === 0 && (
                            <TableRow><TableCell colSpan={totalColSpan} align="center" sx={{ py: 4, color: 'text.secondary' }}>No data to display</TableCell></TableRow>
                        )}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                count={filteredRows.length}
                                page={page}
                                onPageChange={(_, p) => setPage(p)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={[20, 50, 100]}
                                labelDisplayedRows={({ from, to, count }) => `${count} total`}
                                sx={paginationSx}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </div>
    );
}
